import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Textarea,
  Button,
  FormControl,
  Stack,
  FormLabel,
  Input,
  FormHelperText,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { TranscriptLine, parseTranscriptContent } from "../insightsProduct";

export interface TranscriptSubmitInput {
  raw: string;
  parsed: TranscriptLine[] | null;
  speakers: string[];
  parseError: Error | null;
}

export type TranscriptSubmitFn = (
  transcriptInput: TranscriptSubmitInput
) => void;

interface TranscriptFormData {
  transcriptContent: string;
  speakers: string;
}

interface ImportTranscriptModalProps {
  isOpen: boolean;
  onSubmit: TranscriptSubmitFn;
  onClose: () => void;
  transcript: string;
  speakers: string;
}

const ImportTranscriptModal: React.FC<ImportTranscriptModalProps> = (props) => {
  const { isOpen, onSubmit, onClose } = props;
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<TranscriptFormData>({
    defaultValues: {
      transcriptContent: props.transcript,
      speakers: props.speakers,
    },
  });

  const onTranscriptSubmit = (data: TranscriptFormData) => {
    const { speakers, transcriptContent } = data;
    const speakerList = speakers.split(",").map((s) => s.trim());
    let result: TranscriptLine[] | null = null;
    let error = null;
    try {
      result = parseTranscriptContent(speakerList, transcriptContent);
    } catch (err) {
      error = err;
    }
    onSubmit({
      raw: transcriptContent,
      parsed: result,
      speakers: speakerList,
      parseError: error,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW={1000} mr={5} ml={5}>
        <ModalHeader>Import Transcript</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={handleSubmit(onTranscriptSubmit)}>
            <Stack spacing={4} mb={2}>
              <FormControl isInvalid={!!errors.speakers}>
                <FormLabel>Speakers</FormLabel>
                <Input
                  {...register("speakers", {
                    required: "A list of speakers is required",
                  })}
                  placeholder="Jane Doe, John Smith"
                />
                <FormErrorMessage>{errors.speakers?.message}</FormErrorMessage>
                <FormHelperText>
                  Comma separated, case sensitive, list of speakers as they
                  appear in the transcript content (e.g. speaker1, speaker2).
                </FormHelperText>
              </FormControl>
              <FormControl isInvalid={!!errors.transcriptContent}>
                <FormLabel>Transcript Content</FormLabel>
                <Textarea
                  {...register("transcriptContent", {
                    required: "Transcript content is required",
                  })}
                  rows={20}
                />
                <FormErrorMessage>
                  {errors.transcriptContent?.message}
                </FormErrorMessage>
              </FormControl>
              <Button colorScheme="blue" mr={3} type="submit">
                Submit
              </Button>
            </Stack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ImportTranscriptModal;
